import { memo, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import Tippy from '@tippyjs/react';
import { tokens } from '@hotjar/hotsauce';
import { Icon } from '@hotjar/webapp/components/Icon';
import { track } from '@hotjar/webapp/domains/common/tracking/segment';
import { css, keyframes } from '@emotion/react';
import { PlayerButton } from '../PlayerButton';

const StyledPopover = styled(Tippy)`
  &[data-animation='slide-up'][data-state='hidden'] {
    opacity: 0;

    transform: translateY(40px);
  }

  .tippy-content {
    padding: 0;
  }
`;
const Container = styled.div`
  cursor: initial;
  width: 305px;
  height: 111px;
  background-color: ${tokens.color.neutral_700};
  color: ${tokens.color.white};
  display: flex;
  flex-direction: column;
  text-align: center;
  box-shadow: 0px 0 120px 40px rgba(0, 0, 0, 0.4);
`;

const Title = styled.div`
  margin-top: 21px;
  font-size: 14px;
  font-weight: 600;
`;

const Content = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: ${tokens.color.text_default_inverse};
  margin: 7px 25px;
  flex-grow: 1;
  display: flex;
`;

const loaderAnimation = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(0%);
  }
`;

const TIME_TO_HIDE = 5;
const AnimatedLoader = styled.div`
  animation-duration: ${TIME_TO_HIDE}s;
  animation-fill-mode: forwards;
  animation-iteration-count: 1;
  animation-timing-function: linear;
  animation-name: ${loaderAnimation};
  height: 5px;
  width: 100%;

  background: white;
`;

type ForcePlaybackPopupProps = {
  isShown: boolean;
  onClose: () => void;
  siteId: number;
  accountId: number;
  organizationId: number;
};

export const ForcePlaybackPopup = memo(
  ({ isShown, onClose, siteId, accountId, organizationId }: ForcePlaybackPopupProps) => {
    const { t } = useTranslation();

    useEffect(() => {
      if (isShown) {
        track('Element Viewed', {
          element_name: 'playback_notification-playback.forced',
          state_event_was_triggered_on: 'playback.player',
          ui_position: 'playback_notification-bottom.right',
          url_event_was_triggered_on: window.location.href,
          site_id: siteId,
          account_id: accountId,
          organization_id: organizationId,
        });
      }
    }, [isShown, accountId, siteId, organizationId]);

    const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>();
    const handleCloseAction = useCallback(() => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        setTimeoutId(undefined);
      }
      onClose();
    }, [timeoutId, onClose]);

    useLayoutEffect(() => {
      if (isShown && !timeoutId) {
        const newTimeoutId = setTimeout(handleCloseAction, TIME_TO_HIDE * 1000);
        setTimeoutId(newTimeoutId);
      } else if (!isShown && timeoutId) {
        clearTimeout(timeoutId);
        setTimeoutId(undefined);
      }
      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }, [isShown, timeoutId, handleCloseAction]);

    return (
      <StyledPopover
        css={css`
          border-radius: 5px;
          overflow: hidden;
        `}
        offset={[0, 50]}
        zIndex={2000}
        animation="slide-up"
        arrow={false}
        interactive
        placement="bottom-end"
        visible={isShown}
        content={
          <Container>
            <PlayerButton
              css={css`
                padding: 10px;
                margin: 0 0 0 auto;
                border: none;
                position: absolute;
                right: 0;
              `}
              color={tokens.color.white}
              onClick={onClose}
            >
              <Icon size={8} color={tokens.color.white} icon="x" />
            </PlayerButton>
            <Title>{t('common:player.force-playback.title')}</Title>
            <Content>{t('common:player.force-playback.content')}</Content>
            <AnimatedLoader />
          </Container>
        }
      >
        <span />
      </StyledPopover>
    );
  },
);
